import { useRef, useState } from 'react';
import { useWorkspaces } from '../../lib/workspaces';
import { useTranslation } from '../../lib/i18n';
import { WorkspaceBadgeEditor } from './WorkspaceBadgeEditor';
import { WorkspaceNameEditor, WorkspaceNameEditorHandle } from './WorkspaceNameEditor';
import { PageHeader } from './PageHeader';
import styles from './WorkspaceHero.module.css';

// The shared top-of-screen chrome for a workspace's Dashboard — the standard page header (badge +
// editable name + description). A Space is a single screen now (Goals moved to the top-level `/goals`
// screen, the same way Tasks/Routines/Files/Apps already work — see WorkspaceSummaryRow), so there is no
// tab strip to draw beneath it any more.
//
// Looks the workspace up by slug so a keep-alive
// dashboard host (which only knows the slug) still renders the right hero. The hero owns the 48px gap to
// page content.
export function WorkspaceHero({
  slug,
  page,
  descriptionKey = 'workspace.description',
}: {
  slug: string;
  page: string;
  descriptionKey?: string;
}) {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const workspace = workspaces.status === 'ready' ? workspaces.workspaces.find((w) => w.slug === slug) : undefined;
  const name = workspace?.name || slug;
  // The badge's edit state is the name field's state, drawn one slot to the left, so the hero owns the
  // flag and hands the field a way to raise it.
  const [editing, setEditing] = useState(false);
  // An image-update failure belongs beside the name, not on the badge — same placement as native.
  const [imageError, setImageError] = useState<string | null>(null);
  /**
   * True while the badge's file dialog is open (or its upload is in flight). The name field stops
   * committing on blur for that window: opening the OS dialog takes focus off the page, and blur is
   * what closes the field — so without this, clicking "Upload a photo" tore down the whole edit
   * state before the user had chosen anything.
   */
  const [picking, setPicking] = useState(false);
  const nameRef = useRef<WorkspaceNameEditorHandle>(null);
  /** Set by a successful save, so ending the pick doesn't fight the close by grabbing focus back. */
  const savedRef = useRef(false);
  return (
    <div className={styles.hero}>
      <PageHeader
        flush
        avatar={
          workspace ? (
            <WorkspaceBadgeEditor
              workspace={workspace}
              editing={editing}
              page={page}
              onError={setImageError}
              onPickingChange={(next) => {
                setPicking(next);
                // Back from the dialog with nothing saved: the field is still open but the dialog
                // took its focus, so put the caret back and let the user carry on typing.
                if (!next && !savedRef.current) nameRef.current?.focus();
                if (!next) savedRef.current = false;
              }}
              onSaved={() => {
                savedRef.current = true;
                // A photo actually landed — finish the edit the way clicking away would.
                nameRef.current?.commit();
              }}
            />
          ) : undefined
        }
        title={
          <WorkspaceNameEditor
            ref={nameRef}
            id={workspace?.id}
            name={name}
            page={page}
            onEditingChange={setEditing}
            suspendBlurCommit={picking}
          />
        }
        description={t(descriptionKey, { workspace: name })}
      />
      {imageError && <p className={styles.imageError}>{imageError}</p>}
    </div>
  );
}
